import { apiRequest } from './axiosConfig';

// backend が Lead 作成時に Str::random(32) で発行する32桁の英数字（LP・リード共通）。
// ページURL由来の値なので、この形式以外は API を呼ばずに「見つからない」扱いにする。
const REPORT_SLUG_PATTERN = /^[A-Za-z0-9]{32}$/;

export const isValidReportSlug = (slug: string): boolean => REPORT_SLUG_PATTERN.test(slug);

/** スラッグが発行形式でないときに投げる。画面側は 404 と同じ「見つからない」表示にする。 */
export class ReportNotFoundError extends Error {
  constructor(message = 'レポートが見つかりません。') {
    super(message);
    this.name = 'ReportNotFoundError';
    // TS の extends Error + ES5 ターゲット対策（instanceof を効かせる）
    Object.setPrototypeOf(this, ReportNotFoundError.prototype);
  }
}

export interface ReportAnalysis {
  title?: string;
  description?: string;
  domain?: string;
  logo_url?: string | null;
  og_image_url?: string | null;
  colors?: string[];
  category?: string | null;
  [key: string]: any;
}

export interface ReportPlanning {
  features?: string[];
  benefits?: string[];
  brand_name?: string;
  industry?: string;
  competitor_styles?: string[];
  headlines?: string[];
  cta_texts?: string[];
  recommended_messages?: string[];
  target_audience?: string;
  key_selling_points?: string[];
  [key: string]: any;
}

export interface ReportBannerSession {
  status?: string;
  banners?: Array<{
    style_key: string;
    status: string;
    image_url?: string;
  }>;
}

export interface ReportData {
  id: number;
  slug: string;
  lead_id: number;
  report_status: 'pending' | 'processing' | 'completed' | 'failed';
  report_data: {
    service_url?: string;
    // New LP analysis fields
    analysis?: ReportAnalysis;
    planning?: ReportPlanning;
    banner_session_id?: string;
    generated_at?: string;
    // Legacy fields (kept for backward compatibility)
    service_info?: string;
    usp?: string;
    competitor_analysis?: string;
    target_segments?: string[] | string;
    ad_strategy?: string;
    banners?: Array<{
      image_url: string;
      title?: string;
    }>;
    [key: string]: any;
  } | null;
  // New fields from LP API
  banner_session?: ReportBannerSession | null;
  banner_urls?: string[];
  created_at: string;
  updated_at: string;
}

const reportService = {
  /**
   * Get a report by its slug (public endpoint)
   */
  getBySlug: async (slug: string): Promise<ReportData> => {
    if (!isValidReportSlug(slug)) {
      throw new ReportNotFoundError();
    }
    return apiRequest.get<ReportData>(`reports/${encodeURIComponent(slug)}`);
  },
};

export default reportService;
